/**
 * `/m/batches/:batchId` — per-student status after 发布任务.
 *
 * Read-only. Each row shows not-started / in-progress / submitted, done/total,
 * last activity, a playable preview with the AI card, and a link into 回看.
 */
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AudioPlayer, EmptyState, Icon, Tag } from "@xinghai/ui";
import type { IconId, Tone } from "@xinghai/ui";
import { batchesApi, recordingAudioUrl } from "../../lib/api";
import { qk } from "../../lib/queries";
import { AiScoreCard } from "../ai/AiScoreCard";
import { TASK_LABEL as STUDENT_TASK } from "../student/taskMeta";
import { ErrorNotice, LoadingBlock, PageHeader, Section, StatusTag, formatDateTime } from "./common";
import { TASK_LABEL } from "./labels";
import type { TaskStudentStatus } from "./types";

const STATUS_META: Record<TaskStudentStatus, { label: string; icon: IconId; tone: Tone }> = {
  not_started: { label: "未开始", icon: "ui-clock", tone: "neutral" },
  in_progress: { label: "进行中", icon: "ui-microphone", tone: "info" },
  submitted: { label: "已提交", icon: "ui-check-circle", tone: "success" },
};

export function TaskStudentsPage() {
  const { batchId = "" } = useParams();
  const q = useQuery({
    queryKey: qk.batchStudents(batchId),
    queryFn: ({ signal }) => batchesApi.students(batchId, signal),
    enabled: batchId.length > 0,
  });

  const batch = q.data?.batch;
  const title = batch ? batch.tasks.map((t) => TASK_LABEL[t] ?? t).join(" · ") || batch.title : "任务";

  return (
    <div className="xz-adm">
      <PageHeader
        title={title}
        description="发布之后可以看每位学生做到哪了。这里只能看，不能改。"
        actions={
          <Link className="gz-button gz-button--secondary" to="/m/batches">
            返回任务列表
          </Link>
        }
      />
      {q.isPending ? (
        <LoadingBlock rows={3} label="正在加载完成情况" />
      ) : q.isError ? (
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data.students.length === 0 ? (
        <EmptyState title="这个班还没有学生" description="学生注册之后会出现在这里。" />
      ) : (
        <Section title="学生完成情况" subtitle={`${q.data.students.length} 人`}>
          <ul className="xz-stack">
            {q.data.students.map((s) => {
              const meta = STATUS_META[s.status];
              return (
                <li key={s.speaker_id} className="xz-student">
                  <span className="xz-student__name">{s.display_name}</span>
                  <StatusTag icon={meta.icon} tone={meta.tone}>
                    {meta.label}
                  </StatusTag>
                  <span className="gz-caption">
                    {s.done}/{s.total}
                  </span>
                  <span className="gz-caption">
                    最近活动 {s.last_active_at ? formatDateTime(s.last_active_at) : "还没有"}
                  </span>
                  <Link className="gz-button xz-button--sm" to={`/m/speakers/${s.speaker_id}/recordings`}>
                    回看
                  </Link>
                  {s.preview.length > 0 && (
                    <div className="xz-stack">
                      {s.preview.map((rec) => (
                        <div key={rec.id} className="xz-stack">
                          <Tag tone="neutral">
                            <Icon id="ui-microphone" size={16} /> {STUDENT_TASK[rec.task] ?? rec.task}
                          </Tag>
                          <p>{rec.material_text ?? rec.material_title ?? ""}</p>
                          <AiScoreCard ai={rec.ai} text={rec.material_text} />
                          <AudioPlayer
                            src={recordingAudioUrl(rec.id)}
                            hideRate
                            size="sm"
                            label={`播放 ${s.display_name} 的${STUDENT_TASK[rec.task] ?? ""}录音`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}
    </div>
  );
}
